/**
 * Iter-234b — Admin Staffing Roster (Event-level applicant list).
 *
 * Powers the "Staffing" tab in /admin/jobs-v2 EventDetailDrawer.
 * Lists every applicant across every shift in the event, with actions:
 *   • View Profile  · navigates to /admin/talent/:id
 *   • Message       · navigates to /admin/messages?talent=:id
 *   • Shortlist     · toggles is_shortlisted (server-persisted)
 *   • Accept        · books talent via /calendar/bookings/:shift/quick-fill-book
 *   • Decline       · sets applications.status = 'rejected'
 *
 * Renders applicant photo, name, role, city/state, rating, completed shifts,
 * application date, status pill, and per-shift label (since one talent may
 * have applied to multiple shifts in the same event).
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Star, MapPin, Clock, MessageSquare, ExternalLink, Check, X, BookmarkPlus, BookmarkCheck,
  Loader2, Bell, UserCheck,
} from 'lucide-react';
import { formatDate } from '../../utils/timeFormat';
import { toast } from 'sonner';
import { AIMatchScoreBadge } from '../AIBadges';
import GgfBadge from '../GgfBadge';

const STATUS_STYLE = {
  pending:   'bg-amber-50  text-amber-700  border-amber-200',
  accepted:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected:  'bg-rose-50   text-rose-700   border-rose-200',
  withdrawn: 'bg-slate-50  text-slate-600  border-slate-200',
};

// Iter-315e — Talent Confirmation Status describes the TALENT'S action
// (did they commit to working the shift), not the admin's action of
// selecting them. Replaces the misleading "accepted" badge.
const CONFIRMATION_STYLE = {
  awaiting_confirmation: 'bg-red-50 text-red-800 border-red-300',
  confirmed:             'bg-emerald-50 text-emerald-800 border-emerald-300',
  checked_in:            'bg-violet-50 text-violet-800 border-violet-300',
  completed:             'bg-purple-50 text-purple-800 border-purple-300',
  no_show:               'bg-rose-100 text-rose-900 border-rose-400',
  pending:               'bg-amber-50 text-amber-800 border-amber-300',
  declined:              'bg-slate-50 text-slate-700 border-slate-300',
};
const CONFIRMATION_LABEL = {
  awaiting_confirmation: 'Awaiting Confirmation',
  confirmed:             'Confirmed',
  checked_in:            'Checked In',
  completed:             'Checked Out',
  no_show:               'No Show',
  pending:               'Applied',
  declined:              'Declined',
};

const initialsOf = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

export default function AdminStaffingRoster({ shiftIds, eventTitle }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applicants, setApplicants] = useState([]);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('pending'); // pending | shortlisted | accepted | rejected | all
  const [busyId, setBusyId] = useState(null);

  const ids = useMemo(
    () => (Array.isArray(shiftIds) ? shiftIds.filter(Boolean).join(',') : ''),
    [shiftIds],
  );

  const load = useCallback(async () => {
    if (!ids) { setApplicants([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/events/applicants?shift_ids=${ids}`);
      setApplicants(data?.applicants || []);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load applicants');
      setApplicants([]);
    } finally {
      setLoading(false);
    }
  }, [ids]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'shortlisted') return applicants.filter((a) => a.is_shortlisted);
    if (filter === 'all') return applicants;
    if (filter === 'accepted') {
      return applicants.filter((a) => !!a.booking_id);
    }
    if (filter === 'awaiting') {
      // Iter-315f — Awaiting Confirmation quick-filter tab.
      return applicants.filter((a) => a.confirmation_status === 'awaiting_confirmation');
    }
    if (filter === 'pending') {
      return applicants.filter((a) => a.status === 'pending');
    }
    return applicants.filter((a) => a.status === filter);
  }, [applicants, filter]);

  const tally = useMemo(() => ({
    pending: applicants.filter((a) => a.status === 'pending').length,
    shortlisted: applicants.filter((a) => a.is_shortlisted).length,
    // Iter-315e/f — Booked tally = any row with a booking (includes
    // direct-add rows in "Awaiting Confirmation" state).
    accepted: applicants.filter((a) => !!a.booking_id).length,
    rejected: applicants.filter((a) => a.status === 'rejected').length,
    withdrawn: applicants.filter((a) => a.status === 'withdrawn').length,
    // Iter-315f — Awaiting Confirmation sub-count for quick scanning.
    awaiting: applicants.filter((a) => a.confirmation_status === 'awaiting_confirmation').length,
    all: applicants.length,
  }), [applicants]);

  const handleAccept = async (a) => {
    setBusyId(a.application_id);
    try {
      await api.post(
        `/admin/calendar/bookings/${a.shift_id}/quick-fill-book?talent_id=${a.talent_id}`,
      );
      toast.success(`${a.name} booked for this shift`);
      await load();
    } catch (e) {
      toast.error('Could not book talent', { description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setBusyId(null);
    }
  };

  const handleDecline = async (a) => {
    if (!window.confirm(`Decline ${a.name}'s application? This can be reversed by them re-applying.`)) return;
    setBusyId(a.application_id);
    try {
      await api.post(`/admin/applications/${a.application_id}/decline`);
      toast.success(`${a.name} declined`);
      setApplicants((prev) =>
        prev.map((x) => (x.application_id === a.application_id ? { ...x, status: 'rejected' } : x)),
      );
    } catch (e) {
      toast.error('Decline failed', { description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setBusyId(null);
    }
  };

  const handleShortlist = async (a) => {
    const next = !a.is_shortlisted;
    setBusyId(a.application_id);
    try {
      await api.post(`/admin/applications/${a.application_id}/shortlist`, { shortlisted: next });
      setApplicants((prev) =>
        prev.map((x) => (x.application_id === a.application_id ? { ...x, is_shortlisted: next } : x)),
      );
    } catch (e) {
      toast.error('Could not update shortlist');
    } finally {
      setBusyId(null);
    }
  };

  // Iter-315k — One-click Send Reminder. Fires in-app + email + SMS
  // asking the talent to confirm the shift. Server dedups within the
  // auto-cadence buckets; manual sends are always allowed.
  const handleSendReminder = async (a) => {
    if (!a.booking_id) return;
    setBusyId(a.application_id);
    try {
      const { data } = await api.post(`/admin/bookings/${a.booking_id}/send-confirmation-reminder`);
      const channels = (data.channels || []).join(', ');
      toast.success(
        channels
          ? `Reminder sent via ${channels}`
          : 'Reminder queued (delivery may be paused in preview)',
      );
      setApplicants((prev) =>
        prev.map((x) => x.application_id === a.application_id
          ? { ...x, last_reminder_sent_at: new Date().toISOString(), last_reminder_channels: data.channels || [] }
          : x),
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        detail === 'already_confirmed_or_closed'
          ? 'Talent has already confirmed or the booking is closed.'
          : detail || 'Could not send reminder',
      );
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-slate-500 text-xs" data-testid="staffing-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading applicants…
      </div>
    );
  }

  if (err) {
    return (
      <div className="py-3 px-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg" data-testid="staffing-error">
        {err}
      </div>
    );
  }

  if (applicants.length === 0) {
    return (
      <p className="text-xs text-slate-500 italic py-4 text-center" data-testid="staffing-empty">
        No applicants on any shift in this event yet.
      </p>
    );
  }

  const Chip = ({ id, label, count, tone }) => (
    <button
      onClick={() => setFilter(id)}
      data-testid={`staffing-filter-${id}`}
      className={`text-[11px] font-semibold px-2 py-1 rounded-full border transition-colors ${
        filter === id
          ? `${tone} ring-1 ring-cyan-500/40`
          : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
      }`}
    >
      {label}{count != null ? ` · ${count}` : ''}
    </button>
  );

  return (
    <div data-testid="staffing-roster">
      <div className="flex flex-wrap gap-1.5 mb-3">
        <Chip id="pending"     label="Pending"     count={tally.pending}     tone="bg-amber-50 text-amber-700 border-amber-200" />
        <Chip id="shortlisted" label="Shortlist"   count={tally.shortlisted} tone="bg-cyan-50 text-cyan-700 border-cyan-200" />
        <Chip id="accepted"    label="Booked"      count={tally.accepted}    tone="bg-emerald-50 text-emerald-700 border-emerald-200" />
        {tally.awaiting > 0 && (
          <Chip id="awaiting" label="Awaiting Confirmation" count={tally.awaiting} tone="bg-red-50 text-red-800 border-red-300" />
        )}
        <Chip id="rejected"    label="Declined"    count={tally.rejected}    tone="bg-rose-50 text-rose-700 border-rose-200" />
        {tally.withdrawn > 0 && (
          <Chip id="withdrawn" label="Withdrawn" count={tally.withdrawn} tone="bg-slate-100 text-slate-600 border-slate-300" />
        )}
        <Chip id="all"         label="All"         count={tally.all}         tone="bg-slate-100 text-slate-700 border-slate-300" />
      </div>

      <ul className="space-y-2" data-testid="staffing-list">
        {filtered.map((a) => {
          const busy = busyId === a.application_id;
          const status = a.status || 'pending';
          const statusCls = STATUS_STYLE[status] || STATUS_STYLE.pending;
          // Iter-315e — Prefer talent confirmation status over the raw
          // applications.status="accepted" which described the admin's
          // action, not the talent's.
          const confirmationStatus = a.confirmation_status || (a.booking_id ? 'awaiting_confirmation' : status);
          const confirmationLabel = CONFIRMATION_LABEL[confirmationStatus] || String(confirmationStatus).replace(/_/g, ' ');
          const confirmationCls = CONFIRMATION_STYLE[confirmationStatus] || CONFIRMATION_STYLE.pending;
          const confirmationAt = a.confirmation_at || null;
          const confirmationAtLabel = confirmationAt
            ? new Date(confirmationAt).toLocaleString(undefined, {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
              })
            : null;
          return (
            <li
              key={a.application_id}
              className="border border-slate-200 rounded-lg p-2.5 bg-white"
              data-testid={`applicant-${a.application_id}`}
            >
              <div className="flex items-start gap-2.5">
                {/* Avatar */}
                <div className="shrink-0">
                  {a.profile_photo_url ? (
                    <img
                      src={a.profile_photo_url}
                      alt={a.name}
                      className="w-10 h-10 rounded-full object-cover border border-slate-200"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center text-[11px] font-bold text-slate-500 border border-slate-200">
                      {initialsOf(a.name)}
                    </div>
                  )}
                </div>

                {/* Body */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900 truncate" data-testid="applicant-name">
                      {a.name}
                    </span>
                    {a.is_shortlisted && (
                      <BookmarkCheck className="w-3.5 h-3.5 text-cyan-600 shrink-0" aria-label="Shortlisted" />
                    )}
                    {/* Iter-315e — Talent Confirmation Status (what the
                        talent did), not applications.status. Fixes the
                        confusion where "accepted" described the admin's
                        selection instead of the talent's commitment. */}
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${confirmationCls}`}
                      data-testid={`applicant-confirmation-${a.application_id}`}
                      title={confirmationAtLabel ? `${confirmationLabel} · ${confirmationAtLabel}` : confirmationLabel}
                    >
                      {confirmationLabel}
                    </span>
                    {/* Iter-234f — AI Visibility Pass */}
                    <AIMatchScoreBadge
                      score={a.ai_match_score}
                      reasons={a.ai_match_reasons}
                      testId={`applicant-ai-match-${a.application_id}`}
                    />
                    {/* Iter-246 — Guardian Garage Floors cert badge. Admin-only surface. */}
                    {a.ggf_certified && (
                      <GgfBadge
                        certified
                        score={a.ggf_test_score}
                        date={a.ggf_certified_at}
                        source={a.ggf_certified_source}
                        size="xs"
                        context={{ isAdmin: true }}
                      />
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5 flex-wrap">
                    {a.role_name && <span>{a.role_name}</span>}
                    {(a.city || a.state) && (
                      <span className="flex items-center gap-0.5">
                        <MapPin className="w-3 h-3" />
                        {[a.city, a.state].filter(Boolean).join(', ')}
                      </span>
                    )}
                    {a.average_rating != null && (
                      <span className="flex items-center gap-0.5">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        {a.average_rating.toFixed(1)}
                      </span>
                    )}
                    {a.total_shifts_completed > 0 && (
                      <span className="flex items-center gap-0.5">
                        <UserCheck className="w-3 h-3" /> {a.total_shifts_completed} shifts
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                    <Clock className="w-3 h-3" />
                    Applied {a.applied_at ? formatDate(a.applied_at.slice(0, 10)) : '—'} · for{' '}
                    {a.shift_date ? formatDate(a.shift_date) : '—'}
                  </div>

                  {/* Iter-315e — Confirmation-status detail line. Tells
                      staffers at a glance whether the talent has
                      committed (green) or still owes a response (red). */}
                  {confirmationStatus === 'awaiting_confirmation' && (
                    <div
                      className="flex items-center gap-1 text-[11px] font-semibold text-red-700 mt-1"
                      data-testid={`applicant-confirmation-detail-${a.application_id}`}
                    >
                      <Clock className="w-3 h-3" />
                      Awaiting talent confirmation
                      {confirmationAtLabel && (
                        <span className="font-normal text-red-500">
                          · Invitation sent {confirmationAtLabel}
                        </span>
                      )}
                    </div>
                  )}
                  {confirmationStatus === 'confirmed' && confirmationAtLabel && (
                    <div
                      className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 mt-1"
                      data-testid={`applicant-confirmation-detail-${a.application_id}`}
                    >
                      <Check className="w-3 h-3" />
                      Confirmed {confirmationAtLabel}
                    </div>
                  )}

                  {Array.isArray(a.badges) && a.badges.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {a.badges.slice(0, 3).map((b) => (
                        <Badge key={b.code || b.label} variant="outline" className="text-[9px] px-1 py-0 h-4">
                          {b.label}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Task-3 — Message to Client (talent's pitch) */}
                  {a.message_to_client && (
                    <div
                      className="mt-2 rounded-md bg-cyan-50 border border-cyan-100 p-2 text-[12px] text-cyan-900 whitespace-pre-wrap"
                      data-testid={`applicant-message-${a.application_id}`}
                    >
                      <div className="flex items-center gap-1.5 mb-0.5 text-cyan-700">
                        <MessageSquare className="w-3 h-3" />
                        <span className="text-[10px] font-semibold uppercase tracking-wide">Message to Client</span>
                      </div>
                      {a.message_to_client}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px] gap-1"
                      onClick={() => navigate(`/admin/talent/${a.talent_id}`)}
                      data-testid="applicant-view-profile"
                    >
                      <ExternalLink className="w-3 h-3" /> Profile
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px] gap-1"
                      onClick={() => navigate(`/admin/messages/dm/${a.talent_id}${a.shift_id ? `?origin_shift_id=${a.shift_id}` : ''}`)}
                      data-testid="applicant-message"
                    >
                      <MessageSquare className="w-3 h-3" /> Message
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className={`h-7 px-2 text-[11px] gap-1 ${
                        a.is_shortlisted ? 'border-cyan-400 text-cyan-700 bg-cyan-50' : ''
                      }`}
                      disabled={busy}
                      onClick={() => handleShortlist(a)}
                      data-testid="applicant-shortlist"
                    >
                      {a.is_shortlisted ? <BookmarkCheck className="w-3 h-3" /> : <BookmarkPlus className="w-3 h-3" />}
                      {a.is_shortlisted ? 'Saved' : 'Shortlist'}
                    </Button>
                    {/* Iter-315k — Send Reminder appears only on rows
                        awaiting a talent response. Fires in-app + email +
                        SMS asking the talent to confirm. Server rejects
                        the request with 409 once the talent responds. */}
                    {confirmationStatus === 'awaiting_confirmation' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11px] gap-1 border-red-300 text-red-700 hover:bg-red-50"
                        disabled={busy}
                        onClick={() => handleSendReminder(a)}
                        data-testid={`applicant-send-reminder-${a.application_id}`}
                      >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
                        {a.last_reminder_sent_at ? 'Send Again' : 'Send Reminder'}
                      </Button>
                    )}
                    {status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="h-7 px-2 text-[11px] gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          disabled={busy}
                          onClick={() => handleAccept(a)}
                          data-testid="applicant-accept"
                        >
                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Book
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[11px] gap-1 text-rose-700 border-rose-200 hover:bg-rose-50"
                          disabled={busy}
                          onClick={() => handleDecline(a)}
                          data-testid="applicant-decline"
                        >
                          <X className="w-3 h-3" /> Decline
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
